import { useState, useCallback, useEffect, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";

import { SEED_IMGS, LAUNCHED_KEY, G } from "./constants.js";
import {
  readFromDrive, writeToDrive, fetchUserInfo,
  uploadImageToDrive, deleteImageFromDrive, deleteImageFromDriveByName,
  getLastSyncTime, setLastSyncTime,
} from "./storage/DriveStorage.js";

import GoogleAuth      from "./auth/GoogleAuth.jsx";
import BallPhase       from "./components/BallPhase.jsx";
import GridPhase       from "./components/GridPhase.jsx";
import MoodboardPhase  from "./components/MoodboardPhase.jsx";
import ColorPanel      from "./components/ColorPanel.jsx";
import PaletteLibrary  from "./components/PaletteLibrary.jsx";
import StorageSettings from "./components/StorageSettings.jsx";
import OnboardingModal from "./components/OnboardingModal.jsx";

const SCOPES = [
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
].join(" ");

// No starter images  -  library begins empty
const INITIAL_IMAGES = [];

// ── Live-collab: read ?room= from the URL (capability link) ────────────────
// Pure helper, no imported values  -  safe from Rollup TDZ.
function getRoomParam() {
  try { return new URLSearchParams(window.location.search).get("room") || null; }
  catch { return null; }
}

function orbSize(count) {
  if (count <= 10)  return 240;
  if (count <= 30)  return 280;
  if (count <= 60)  return 320;
  if (count <= 100) return 360;
  return 400;
}

// ── localStorage helpers ───────────────────────────────────────────────────
const LS = {
  get:     k => { try { return localStorage.getItem(k); } catch { return null; } },
  set:     (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  del:     k => { try { localStorage.removeItem(k); } catch {} },
  getJSON: k => { try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : null; } catch { return null; } },
  setJSON: (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch {} },
};

// ── Compress image (canvas → JPEG) ─────────────────────────────────────────
function compressImage(file, maxPx = 1200, quality = 0.78) {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width: w, height: h } = img;
      if (w > h && w > maxPx) { h = Math.round(h * maxPx / w); w = maxPx; }
      else if (h > maxPx) { w = Math.round(w * maxPx / h); h = maxPx; }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const reader = new FileReader();
      reader.onload = e => resolve(e.target.result);
      reader.readAsDataURL(file);
    };
    img.src = url;
  });
}

// ── Fetch web image → compress → base64 ─────────────────────────────────
// Tries the same-origin CORS proxy first (works for hotlink/non-CORS hosts),
// then a direct fetch. Caching at add-time means web images render in export
// and survive the original URL going dead later.
async function fetchAndCompressUrl(url) {
  const tryFetch = async (u) => {
    const res = await fetch(u);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const file = new File([blob], "web-image.jpg", { type: blob.type });
    return await compressImage(file, 1200, 0.82); // ~60-80KB JPEG
  };
  try { const viaImg = await tryFetch("/api/img?url=" + encodeURIComponent(url)); if (viaImg) return viaImg; } catch {}
  try { const viaProxy = await tryFetch("/api/proxy?url=" + encodeURIComponent(url)); if (viaProxy) return viaProxy; } catch {}
  try { return await tryFetch(url); } catch {}
  return null; // all failed — URL-only fallback (still shows on board)
}

function formatSyncTime(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  const hrs  = Math.floor(diff / 3600000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24)  return `${hrs}h ago`;
  return new Date(isoString).toLocaleDateString();
}

export default function App() {
  const [token,          setToken]          = useState(null);
  const [tokenExpiry,    setTokenExpiry]    = useState(null);
  const [user,           setUser]           = useState(null);
  const [phase,          setPhase]          = useState("auth");
  const [images,         setImages]         = useState(() => {
    const cached = LS.getJSON("oracle_cache");
    // Filter out any old seed images from cache
    const cached_imgs = cached?.images?.filter(i => i.sourceType !== "Seed") || [];
    return cached_imgs.length ? cached_imgs : [];
  });
  const [savedPalettes,  setSavedPalettes]  = useState(() => LS.getJSON("oracle_cache")?.palettes || []);
  const [savedMoodboards,setSavedMoodboards]= useState(() => LS.getJSON("oracle_cache")?.moodboards || []);
  const [collections,    setCollections]    = useState(() => LS.getJSON("oracle_cache")?.collections || []);
  const [activeCollection, setActiveCollection] = useState(null); // null = All
  const [selected,       setSelected]       = useState([]);
  const [moodItems,      setMoodItems]      = useState([]);
  const [loadedBoard,    setLoadedBoard]    = useState(null); // full board payload to restore (links/bg/etc.)
  const [colorTarget,    setColorTarget]    = useState(null);
  const [showLibrary,    setShowLibrary]    = useState(false);
  const [showSettings,   setShowSettings]   = useState(false);
  const [addOpen,        setAddOpen]        = useState(false);
  const [pasteUrl,       setPasteUrl]       = useState("");
  const [syncIndicator,  setSyncIndicator]  = useState(null); // "saving"|"saved"|"error"
  const [lastSynced,     setLastSynced]     = useState(() => getLastSyncTime());
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const syncTimerRef = useRef(null);

  // Update "X mins ago" every minute
  useEffect(() => {
    const t = setInterval(() => setLastSynced(getLastSyncTime()), 60000);
    return () => clearInterval(t);
  }, []);

  // Always-current mirror  -  used to read latest images inside callbacks without stale closures
  const imagesRef = useRef([]);
  useEffect(() => { imagesRef.current = images; }, [images]);
  const collectionsRef = useRef([]);
  useEffect(() => { collectionsRef.current = collections; }, [collections]);

  // IDs of newly added images awaiting a quick tag prompt
  const [pendingTagIds, setPendingTagIds] = useState([]);


  const [hasLaunched, setHasLaunched] = useState(() => LS.get(LAUNCHED_KEY) === "true");
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Capability link  -  if present we force into the moodboard to auto-join
  const [roomParam] = useState(() => getRoomParam());
  const markLaunched = useCallback(() => { LS.set(LAUNCHED_KEY, "true"); setHasLaunched(true); }, []);

  const setPhaseWithMemory = useCallback(p => {
    setPhase(p);
    if (p === "grid" || p === "ball") LS.set("oracle_last_phase", p);
  }, []);

  const getValidToken = useCallback(() => {
    if (!token) return null;
    if (tokenExpiry && Date.now() > tokenExpiry - 60_000) return null;
    return token;
  }, [token, tokenExpiry]);

  // ── Restore session ────────────────────────────────────────────────────
  useEffect(() => {
    const cachedToken  = LS.get("oracle_token");
    const cachedExpiry = parseInt(LS.get("oracle_token_expiry") || "0", 10);
    const cachedUser   = LS.getJSON("oracle_user");
    const lastPhase    = LS.get("oracle_last_phase") || "ball";

    if (cachedToken && cachedExpiry > Date.now() + 60_000) {
      setToken(cachedToken);
      setTokenExpiry(cachedExpiry);
      if (cachedUser) setUser(cachedUser);
      if (roomParam) { setMoodItems([]); setPhase("moodboard"); }
      else           setPhaseWithMemory(lastPhase === "grid" ? "grid" : "ball");
      backgroundRefresh(cachedToken);
    } else {
      setPhaseWithMemory("auth");
    }
  }, []); // eslint-disable-line

  const backgroundRefresh = async tok => {
    try {
      const [info, saved] = await Promise.all([fetchUserInfo(tok), readFromDrive(tok)]);
      if (info) { setUser(info); LS.setJSON("oracle_user", info); }
      if (saved) {
        if (saved.images) saved.images = saved.images.filter(i => i.sourceType !== "Seed");
        if (saved.collections?.length) { setCollections(saved.collections); }
        if (saved.images?.length) {
          // Detect newly added images from extension (in Drive but not in current state)
          const currentIds = new Set(imagesRef.current.map(i => i.id));
          const newFromExt = saved.images.filter(i => !currentIds.has(i.id) && i.sourceType === "Extension");
          setImages(saved.images);
          // Prompt to tag new extension-saved images
          if (newFromExt.length > 0) setPendingTagIds(ids => [...new Set([...ids, ...newFromExt.map(i => i.id)])]);
        }
        if (saved.palettes?.length)   setSavedPalettes(saved.palettes);
        if (saved.moodboards?.length) setSavedMoodboards(saved.moodboards);
        LS.setJSON("oracle_cache", saved);
      }
      // Show onboarding for brand new users (no images yet)
      if (!hasLaunched && !imagesRef.current.length) setShowOnboarding(true);
    } catch(e) { console.warn("Background refresh failed:", e); }
  };

  // ── Persist metadata to Drive (never stores base64) ────────────────────
  const persistData = useCallback(async (imgs, pals, moods, cols) => {
    const tok = getValidToken(); if (!tok) return;
    setSyncIndicator("saving");

    // Cache locally first  -  instant
    const colsToSave = cols !== undefined ? cols : collectionsRef.current;
    const data = { images:imgs, palettes:pals, moodboards:moods, collections:colsToSave, version:2 };
    LS.setJSON("oracle_cache", data);

    const ok = await writeToDrive(tok, data);
    if (ok) {
      setLastSyncTime();
      setLastSynced(getLastSyncTime());
      setSyncIndicator("saved");
    } else {
      setSyncIndicator("error");
    }
    clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => setSyncIndicator(null), ok ? 2500 : 5000);
  }, [getValidToken]);

  // ── Auto-refresh: poll Drive when tab becomes visible (after backgroundRefresh defined) ──
  useEffect(() => {
    let pollTimer = null;
    const doRefresh = () => {
      const tok = getValidToken();
      if (tok) backgroundRefresh(tok);
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        const last = getLastSyncTime();
        const age  = last ? (Date.now() - new Date(last).getTime()) : Infinity;
        if (age > 45000) doRefresh();
        clearInterval(pollTimer);
        pollTimer = setInterval(doRefresh, 60000);
      } else {
        clearInterval(pollTimer);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(pollTimer); };
  }, []); // eslint-disable-line

  // ── Auth ───────────────────────────────────────────────────────────────
  const handleToken = useCallback(async (tok, expiresIn) => {
    const expiry = Date.now() + (expiresIn||3600) * 1000;
    setToken(tok); setTokenExpiry(expiry);
    LS.set("oracle_token", tok);
    LS.set("oracle_token_expiry", String(expiry));
    setPhaseWithMemory("loading");
    try {
      const [info, saved] = await Promise.all([fetchUserInfo(tok), readFromDrive(tok)]);
      if (info) { setUser(info); LS.setJSON("oracle_user", info); }
      if (saved) {
        // Strip any old seed images that may still be in Drive
        if (saved.images) saved.images = saved.images.filter(i => i.sourceType !== "Seed");
        if (saved.images?.length)       setImages(saved.images);
        if (saved.palettes?.length)     setSavedPalettes(saved.palettes);
        if (saved.moodboards?.length)   setSavedMoodboards(saved.moodboards);
        // Always set collections (even empty) so mobile sees them on first login
        if (saved.collections)          setCollections(saved.collections);
        LS.setJSON("oracle_cache", saved);
      }
    } catch(e) { console.warn("Load failed:", e); }
    if (roomParam) { setMoodItems([]); setPhase("moodboard"); return; }
    const lastPhase = LS.get("oracle_last_phase") || "ball";
    setPhaseWithMemory(lastPhase === "grid" ? "grid" : "ball");
  }, [setPhaseWithMemory, roomParam]);

  // ── Image actions ──────────────────────────────────────────────────────
  const toggleSelect = id => {
    if (id === "clear") { setSelected([]); return; }
    setSelected(p => p.includes(id) ? p.filter(x=>x!==id) : [...p, id]);
  };

  const buildMoodboard = () => {
    setMoodItems(images.filter(i=>selected.includes(i.id)).map((img,idx)=>({
      ...img, type:"image",
      x: 48+(idx%3)*280, y: 48+Math.floor(idx/3)*220, w: 240,
    })));
    setPhaseWithMemory("moodboard");
  };

  // ── Extract smart tags from a URL ─────────────────────────────────────
  const extractTagsFromUrl = (imageUrl, pageUrl) => {
    const tags = [];
    const addFromPath = urlStr => {
      try {
        const u = new URL(urlStr);
        if (u.hostname.includes("google.")) {
          const q = u.searchParams.get("q");
          if (q) tags.push(...q.toLowerCase().split(/[\s+,\-_]+/).filter(t=>t.length>2).slice(0,4));
          return;
        }
        // Extract meaningful path segments (skip UUIDs, hashes, pure numbers, single words like "pin")
        const segs = u.pathname.split("/")
          .map(s => decodeURIComponent(s).replace(/[-_]/g," ").replace(/\.[a-z]+$/i,"").trim())
          .filter(s => s.length > 3 && !/^[\da-f-]{8,}$/i.test(s) && !/^\d+$/.test(s) && s.split(" ").length > 1);
        tags.push(...segs.slice(0, 4));
      } catch {}
    };
    // 1. Try page URL first (richest info  -  article slug, search query)
    if (pageUrl) addFromPath(pageUrl);
    // 2. Try image URL (sometimes has descriptive filename)
    if (imageUrl) addFromPath(imageUrl);
    // 3. Always include domain as a guaranteed tag fallback
    try {
      const domain = new URL(pageUrl || imageUrl).hostname.replace("www.","").split(".")[0];
      if (domain && domain.length > 2) tags.push(domain);
    } catch {}
    return [...new Set(tags.map(t=>t.toLowerCase().trim()).filter(t=>t.length>2))].slice(0,5);
  };

  const addUrl = () => {
    if (!pasteUrl.trim()) return;
    const rawUrl = pasteUrl.trim();
    const domain = (() => { try { return new URL(rawUrl).hostname.replace("www.",""); } catch { return "web"; } })();
    const autoTags = extractTagsFromUrl(rawUrl, null); // URL paste: no page context
    const tempId = Date.now();
    // Add immediately so user sees it right away
    const baseTags = autoTags.length > 0 ? autoTags : ["custom"];
    const newImg = {
      id: tempId, url: rawUrl, tag: baseTags[0] || "custom", tags: baseTags,
      source: domain, sourceType:"URL paste",
      sourceUrl: rawUrl,    // ↗ link target  -  the pasted URL itself
      directUrl: rawUrl,    // kept for Drive fetch reference (not for ↗ link)
      addedAt: Date.now(), saved: false,
    };
    const nextAfterAdd = [newImg, ...imagesRef.current];
    setImages(nextAfterAdd);
    persistData(nextAfterAdd, savedPalettes, savedMoodboards);
    setPasteUrl(""); setAddOpen(false);

    // Background: try to fetch + compress → upload to Drive oracle-images/ as separate file
    fetchAndCompressUrl(rawUrl).then(async base64 => {
      if (!base64) return; // CORS blocked  -  URL-only is fine
      const tok = getValidToken();
      let driveResult = null;
      if (tok) {
        driveResult = await uploadImageToDrive(tok, base64, `img_${tempId}.jpg`).catch(() => null);
      }
      const updImg = driveResult
        ? { driveFileId: driveResult.driveFileId } // keep base64 url for canvas DNA compat
        : {};  // CORS-blocked  -  url stays as original
      const next = imagesRef.current.map(i => i.id === tempId ? { ...i, ...updImg } : i);
      setImages(next);
      persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
    });
  };

  // ── B18: add an image chosen from web search results ──
  // Reuses the same immediate-add + background fetch→compress→Drive pipeline as addUrl.
  const addWebImage = useCallback((result, query = "") => {
    const full = result?.full;
    if (!full) return;
    const pageUrl = result?.source || full;
    const domain = (() => { try { return new URL(pageUrl).hostname.replace("www.",""); } catch { return "web"; } })();
    const qTags = String(query).toLowerCase().split(/\s+/).map(s => s.replace(/[^a-z0-9-]/g,"")).filter(s => s.length > 2);
    const tags = Array.from(new Set([...qTags, domain])).slice(0, 6);
    const baseTags = tags.length ? tags : ["web"];
    const tempId = Date.now() + Math.floor(Math.random() * 1000);
    const newImg = {
      id: tempId, url: full, tag: baseTags[0], tags: baseTags,
      source: domain, sourceType: "Web search",
      sourceUrl: pageUrl,   // ↗ opens the page the image came from
      directUrl: full,
      addedAt: Date.now(), saved: false,
    };
    const nextAfterAdd = [newImg, ...imagesRef.current];
    setImages(nextAfterAdd);
    persistData(nextAfterAdd, savedPalettes, savedMoodboards, collectionsRef.current);

    fetchAndCompressUrl(full).then(async base64 => {
      if (!base64) return; // CORS blocked — URL-only is fine
      const tok = getValidToken();
      let driveResult = null;
      if (tok) driveResult = await uploadImageToDrive(tok, base64, `img_${tempId}.jpg`).catch(() => null);
      const updImg = driveResult ? { driveFileId: driveResult.driveFileId } : {};
      const next = imagesRef.current.map(i => i.id === tempId ? { ...i, ...updImg } : i);
      setImages(next);
      persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
    });
  }, [savedPalettes, savedMoodboards]);

  const handleSave = id => {
    const next = imagesRef.current.map(i => i.id === id ? { ...i, saved: !i.saved } : i);
    setImages(next);
    persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
  };

  const handleDeleteImage = useCallback(id => {
    const img = imagesRef.current.find(i => i.id === id);
    const tok = getValidToken();
    if (tok) {
      if (img?.driveFileId) {
        // Fast path: we have the file ID  -  delete directly
        deleteImageFromDrive(tok, img.driveFileId).catch(() => {});
      } else {
        // Fallback: search oracle-images/ for img_{id}.jpg  -  catches async-upload-before-close case
        deleteImageFromDriveByName(tok, `img_${id}.jpg`).catch(() => {});
      }
    }
    const next = imagesRef.current.filter(i => i.id !== id);
    setImages(next);
    persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
    setSelected(p => p.filter(x => x !== id));
  }, [savedPalettes, savedMoodboards, persistData, getValidToken]);

  // Bulk delete with confirmation
  const handleBulkDelete = useCallback(() => {
    setBulkDeleteConfirm(true);
  }, []);

  const confirmBulkDelete = useCallback(() => {
    // Delete Drive files for all selected images that have driveFileId
    const tok = getValidToken();
    if (tok) {
      imagesRef.current
        .filter(i => selected.includes(i.id))
        .forEach(i => {
          if (i.driveFileId) {
            deleteImageFromDrive(tok, i.driveFileId).catch(() => {});
          } else {
            deleteImageFromDriveByName(tok, `img_${i.id}.jpg`).catch(() => {});
          }
        });
    }
    const next = imagesRef.current.filter(i => !selected.includes(i.id));
    setImages(next);
    persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
    setSelected([]);
    setBulkDeleteConfirm(false);
  }, [selected, savedPalettes, savedMoodboards, persistData, getValidToken]);

  const handleSavePalette = pal => {
    const next = [...savedPalettes, pal];
    setSavedPalettes(next); persistData(images, next, savedMoodboards, collectionsRef.current); setColorTarget(null);
  };

  const handleRenamePalette = useCallback((id, name) => {
    const next = savedPalettes.map(p => p.id === id ? { ...p, name } : p);
    setSavedPalettes(next);
    persistData(images, next, savedMoodboards, collectionsRef.current);
  }, [images, savedMoodboards, persistData]);

  const handleDeletePalette = id => {
    const next = savedPalettes.filter(x=>x.id!==id);
    setSavedPalettes(next); persistData(images, next, savedMoodboards, collectionsRef.current);
  };

  const handleUpdateTags = updated => {
    const next = imagesRef.current.map(i => i.id === updated.id ? updated : i);
    setImages(next);
    persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
  };

  // ── Desktop file drop  -  compress → show immediately → upload to Drive ────
  const handleDropFiles = useCallback((files, onProgress) => {
    const total = files.length;

    const processAll = async () => {
      const results = [];
      const tok = getValidToken();

      for (let idx = 0; idx < files.length; idx++) {
        const file = files[idx];
        const compressed = await compressImage(file, 1200, 0.82); // higher quality for desktop
        const rawName = file.name.replace(/\.[^.]+$/, "").toLowerCase().replace(/[-_]/g, " ").replace(/\s+/g," ").trim();
        // Filter out generic camera filenames like IMG_001, DSC_0001, Screenshot 2024...
        const isGeneric = /^(img|dsc|dcim|pic|photo|image|screenshot|screen shot|capture|clip|file)[\s_\-]?\d*/i.test(rawName) || /^\d+$/.test(rawName);
        const name = isGeneric ? "" : rawName;
        const dropTags = name ? [name, "desktop"] : ["desktop"];
        const tempId = Date.now() + idx * 1000 + Math.floor(Math.random() * 999);
        results.push({
          id: tempId,
          url: compressed,       // base64 for immediate display
          tag: name || "desktop",
          tags: dropTags,
          source: "Desktop",
          sourceType: "Desktop drop",
          sourceUrl: null,
          addedAt: Date.now(),
          saved: false,
        });
        if (onProgress) onProgress(idx + 1, total);
      }

      // Show immediately (base64)  -  no stale closure: use imagesRef
      const withBase64 = [...results, ...imagesRef.current];
      setImages(withBase64);
      persistData(withBase64, savedPalettes, savedMoodboards, collectionsRef.current);
      if (onProgress) onProgress(total, total);
      // Prompt user to tag the new images
      if (results.length > 0) setPendingTagIds(results.map(r => r.id));

      // Background: upload each to Drive oracle-images/ folder as real JPEG file
      if (tok) {
        for (const img of results) {
          uploadImageToDrive(tok, img.url, `img_${img.id}.jpg`)
            .then(driveResult => {
              if (!driveResult) return;
              // Keep base64 as display url  -  Drive thumbnail URLs block canvas DNA
              const next = imagesRef.current.map(i =>
                i.id === img.id
                  ? { ...i, driveFileId: driveResult.driveFileId }
                  : i
              );
              setImages(next);
              persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
            })
            .catch(() => {}); // silently keep base64 if upload fails
        }
      }
    };

    processAll();
  }, [savedPalettes, savedMoodboards, persistData, getValidToken]);

  // ── Moodboard ─────────────────────────────────────────────────────────
  const handleSaveMoodboard = useCallback(mb => {
    // If mb.id matches an existing one, overwrite it; otherwise append
    const exists = savedMoodboards.some(x => x.id === mb.id);
    const next = exists
      ? savedMoodboards.map(x => x.id === mb.id ? mb : x)
      : [...savedMoodboards, mb];
    setSavedMoodboards(next); persistData(images, savedPalettes, next, collectionsRef.current);
  }, [savedMoodboards, images, savedPalettes, persistData]);

  const handleLoadMoodboard = useCallback(mb => {
    setMoodItems(mb.items || []); setLoadedBoard({ ...mb, _loadAt: Date.now() }); setPhaseWithMemory("moodboard");
  }, [setPhaseWithMemory]);

  const handleDeleteMoodboard = useCallback(id => {
    const next = savedMoodboards.filter(x=>x.id!==id);
    setSavedMoodboards(next); persistData(images, savedPalettes, next, collectionsRef.current);
  }, [savedMoodboards, images, savedPalettes, persistData]);

  // ── Collection handlers ────────────────────────────────────────────────
  const handleCreateCollection = useCallback((name, initialImageIds = []) => {
    const col = { id: Date.now(), name: name.trim() || "New Collection", imageIds: [...new Set(initialImageIds)], createdAt: new Date().toLocaleDateString() };
    const next = [...collectionsRef.current, col];
    setCollections(next);
    persistData(imagesRef.current, savedPalettes, savedMoodboards, next);
    return col.id;
  }, [savedPalettes, savedMoodboards, persistData]);

  const handleRenameCollection = useCallback((id, name) => {
    const next = collectionsRef.current.map(c => c.id === id ? { ...c, name } : c);
    setCollections(next);
    persistData(imagesRef.current, savedPalettes, savedMoodboards, next);
  }, [savedPalettes, savedMoodboards, persistData]);

  const handleDeleteCollection = useCallback(id => {
    const next = collectionsRef.current.filter(c => c.id !== id);
    setCollections(next);
    if (activeCollection === id) setActiveCollection(null);
    persistData(imagesRef.current, savedPalettes, savedMoodboards, next);
  }, [savedPalettes, savedMoodboards, persistData, activeCollection]);

  const handleAddToCollection = useCallback((colId, imageIds) => {
    const next = collectionsRef.current.map(c =>
      c.id === colId ? { ...c, imageIds: [...new Set([...c.imageIds, ...imageIds])] } : c
    );
    setCollections(next);
    persistData(imagesRef.current, savedPalettes, savedMoodboards, next);
  }, [savedPalettes, savedMoodboards, persistData]);

  const handleRemoveFromCollection = useCallback((colId, imageId) => {
    const next = collectionsRef.current.map(c =>
      c.id === colId ? { ...c, imageIds: c.imageIds.filter(i => i !== imageId) } : c
    );
    setCollections(next);
    persistData(imagesRef.current, savedPalettes, savedMoodboards, next);
  }, [savedPalettes, savedMoodboards, persistData]);

  const handleSyncNow = useCallback(() => {
    persistData(imagesRef.current, savedPalettes, savedMoodboards, collectionsRef.current);
  }, [savedPalettes, savedMoodboards, persistData]);

  // ── Drive file verification  -  user-triggered, checks if backup JPEGs still exist ──
  const handleVerifyDriveFiles = useCallback(async () => {
    const tok = getValidToken();
    if (!tok) return;
    const withDriveId = imagesRef.current.filter(i => i.driveFileId);
    if (!withDriveId.length) return;

    const gone = [];
    for (const img of withDriveId) {
      try {
        const r = await fetch(
          `https://www.googleapis.com/drive/v3/files/${img.driveFileId}?fields=id,trashed`,
          { headers: { Authorization: `Bearer ${tok}` } }
        );
        if (!r.ok || (await r.json()).trashed) gone.push(img.id);
      } catch { gone.push(img.id); }
    }

    if (!gone.length) {
      alert("All Drive backup files are intact.");
      return;
    }

    if (window.confirm(`${gone.length} image${gone.length>1?"s":""} have been deleted from Google Drive. Remove them from your ORACLE library too?`)) {
      const next = imagesRef.current.filter(i => !gone.includes(i.id));
      setImages(next);
      persistData(next, savedPalettes, savedMoodboards, collectionsRef.current);
    }
  }, [savedPalettes, savedMoodboards, persistData, getValidToken]);

  const handleSignOut = () => {
    setToken(null); setTokenExpiry(null); setUser(null);
    setImages(INITIAL_IMAGES); setSavedPalettes([]); setSavedMoodboards([]);
    setPhaseWithMemory("auth"); setShowSettings(false);
    ["oracle_token","oracle_token_expiry","oracle_user","oracle_cache",
     "oracle_drive_structure","oracle_last_sync","oracle_last_phase"].forEach(k => LS.del(k));
  };

  const userImageCount = images.filter(i => i.sourceType !== "Seed").length;
  const currentOrbSize = orbSize(userImageCount || images.length);
  const syncLabel = lastSynced ? `Synced ${formatSyncTime(lastSynced)}` : "Not synced yet";

  // ── Render ─────────────────────────────────────────────────────────────
  if (phase === "auth") return <GoogleAuth onToken={handleToken}/>;

  if (phase === "loading") return (
    <div style={{ minHeight:"100vh", background:"#070710", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <style>{G}</style>
      <div style={{ textAlign:"center" }}>
        <div style={{ width:"32px", height:"32px", borderRadius:"50%", border:"2px solid rgba(232,255,71,.12)", borderTop:"2px solid #E8FF47", animation:"spin .8s linear infinite", margin:"0 auto 16px" }}/>
        <p style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.2)", letterSpacing:".14em", textTransform:"uppercase" }}>Loading your library...</p>
      </div>
    </div>
  );

  if (phase === "ball" || phase === "bursting") return (
    <div>
      <style>{G}</style>
      <BallPhase
        phase={phase}
        orbSize={currentOrbSize}
        onEnter={()=>{setPhaseWithMemory("bursting");setTimeout(()=>{setPhaseWithMemory("grid");markLaunched();},750);}}
        onSkip={()=>{setPhaseWithMemory("grid");markLaunched();}}
      onShowOnboarding={() => setShowOnboarding(true)}
        isReturning={hasLaunched}
        images={images.length > 0 ? images : (LS.getJSON("oracle_cache")?.images || [])}
        imageCount={userImageCount || images.length}
      />
    </div>
  );

  if (phase === "moodboard") return (
    <MoodboardPhase
      items={moodItems} setItems={setMoodItems}
      loadedBoard={loadedBoard}
      onBack={()=>setPhaseWithMemory("grid")}
      savedMoodboards={savedMoodboards}
      onSaveMoodboard={handleSaveMoodboard}
      onLoadMoodboard={handleLoadMoodboard}
      onDeleteMoodboard={handleDeleteMoodboard}
      libraryImages={images}
      collections={collections}
      user={user}
      initialRoom={roomParam}
      token={token}
    />
  );

  return (
    <div>
      <style>{G}</style>

      {/* Sync indicator */}
      {syncIndicator && (
        <div style={{ position:"fixed", bottom:"24px", right:"24px", zIndex:200, padding:"8px 16px", borderRadius:"8px", background:syncIndicator==="error"?"rgba(255,80,80,.15)":"rgba(7,7,16,.95)", border:"1px solid "+(syncIndicator==="saved"?"rgba(232,255,71,.3)":syncIndicator==="error"?"rgba(255,80,80,.3)":"rgba(255,255,255,.1)"), fontFamily:"'DM Mono'", fontSize:"10px", color:syncIndicator==="saved"?"#E8FF47":syncIndicator==="error"?"rgba(255,120,120,.8)":"rgba(255,255,255,.4)", letterSpacing:".1em", textTransform:"uppercase", animation:"fade-in .2s ease" }}>
          {syncIndicator==="saving"?"Saving to Drive...":syncIndicator==="saved"?"Saved to Drive ✓":"Drive save failed  -  data preserved locally"}
        </div>
      )}

      {/* Bulk delete confirmation */}
      {bulkDeleteConfirm && (
        <div onClick={()=>setBulkDeleteConfirm(false)} style={{ position:"fixed", inset:0, zIndex:600, background:"rgba(0,0,0,.85)", backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"center", animation:"fade-in .2s ease" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:"#0d0d1e", border:"1px solid rgba(255,80,80,.25)", borderRadius:"16px", padding:"32px", width:"360px", textAlign:"center", boxShadow:"0 40px 80px rgba(0,0,0,.9)" }}>
            <p style={{ fontFamily:"'Bebas Neue'", fontSize:"24px", letterSpacing:".15em", color:"rgba(255,120,120,.9)", marginBottom:"10px" }}>DELETE {selected.length} IMAGES?</p>
            <p style={{ fontFamily:"'DM Mono'", fontSize:"10px", color:"rgba(255,255,255,.3)", lineHeight:"1.8", marginBottom:"28px" }}>
              This removes {selected.length === 1 ? "this image" : `these ${selected.length} images`} from your library and Google Drive permanently. Cannot be undone.
            </p>
            <div style={{ display:"flex", gap:"10px", justifyContent:"center" }}>
              <button onClick={()=>setBulkDeleteConfirm(false)} style={{ padding:"10px 22px", borderRadius:"8px", border:"1px solid rgba(255,255,255,.1)", background:"transparent", color:"rgba(255,255,255,.4)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer" }}>Cancel</button>
              <button onClick={confirmBulkDelete} style={{ padding:"10px 22px", borderRadius:"8px", border:"none", background:"rgba(255,60,60,.2)", color:"rgba(255,120,120,.9)", fontFamily:"'DM Mono'", fontSize:"10px", letterSpacing:".08em", textTransform:"uppercase", cursor:"pointer", fontWeight:"600" }}>Delete All</button>
            </div>
          </div>
        </div>
      )}

      <GridPhase
        images={images}
        selected={selected}
        onToggle={toggleSelect}
        onBuildMoodboard={buildMoodboard}
        onSave={handleSave}
        onDeleteImage={handleDeleteImage}
        onBulkDelete={handleBulkDelete}
        onColorDna={setColorTarget}
        addOpen={addOpen}
        setAddOpen={setAddOpen}
        pasteUrl={pasteUrl}
        setPasteUrl={setPasteUrl}
        onAddUrl={addUrl}
        onAddWebImage={addWebImage}
        onBackToBall={()=>{setPhaseWithMemory("bursting");setTimeout(()=>setPhaseWithMemory("ball"),50);}}
        savedPalettes={savedPalettes}
        onShowLibrary={()=>setShowLibrary(true)}
        onShowSettings={()=>setShowSettings(true)}
        storageLabel={user?`Drive [${user.email?.split("@")[0]}]`:"Drive"}
        syncLabel={syncLabel}
        onSyncNow={handleSyncNow}
        onShowOnboarding={() => setShowOnboarding(true)}
        savedMoodboards={savedMoodboards}
        activeMoodItems={moodItems}
        pendingTagIds={pendingTagIds}
        onClearPendingTags={() => setPendingTagIds([])}
        collections={collections}
        activeCollection={activeCollection}
        onSetActiveCollection={setActiveCollection}
        onCreateCollection={handleCreateCollection}
        onRenameCollection={handleRenameCollection}
        onDeleteCollection={handleDeleteCollection}
        onAddToCollection={handleAddToCollection}
        onRemoveFromCollection={handleRemoveFromCollection}
        onUpdateTags={handleUpdateTags}
        onDropFiles={handleDropFiles}
        onLoadMoodboard={handleLoadMoodboard}
        onDeleteMoodboard={handleDeleteMoodboard}
        isFirstVisit={!hasLaunched && userImageCount === 0}
      />
      {colorTarget  && <ColorPanel img={colorTarget} onClose={()=>setColorTarget(null)} onSave={handleSavePalette}/>}
      {showLibrary  && <PaletteLibrary palettes={savedPalettes} onClose={()=>setShowLibrary(false)} onDelete={handleDeletePalette} onRename={handleRenamePalette}/>}
      {showOnboarding && <OnboardingModal user={user} onDone={() => { setShowOnboarding(false); LS.set(LAUNCHED_KEY, "true"); setHasLaunched(true); }}/>}
      {showSettings && <StorageSettings user={user} onSignOut={handleSignOut} onClose={()=>setShowSettings(false)} syncLabel={syncLabel} onVerifyDriveFiles={handleVerifyDriveFiles}/>}
    </div>
  );
}
